import * as React from "react";
import { CellContext } from "@tanstack/react-table";
import styled, { css } from "styled-components";
import _ from "lodash";
import { useHistory } from "react-router-dom";

import {
  FilterParams,
  ITerminology,
  PaginatedParams
} from "src/library/utils";
import { ActionButton } from "src/library/components/Button";
import { Table } from "src/core/dashboard/components/widgets";
import {
  useNotificationsApi,
  INotification
} from "src/core/notifications/api/notifications.api";
import { getNotificationTableColumns } from "src/core/shared/components/tables/notificationManagementTableColumns";
import { UserMultiSelect } from "src/core/users/components/UserMultiSelect";
import { Select } from "src/library/components/Forms";
import { CustomerMultiSelect } from "src/core/customers/components/CustomerMultiSelect";
import { Text } from "src/library/components/Text";
import { DropMultiSelect } from "src/core/drops/components/DropMultiSelect";

const defaultPagination: PaginatedParams<INotification> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "createdAt", "DESC" ] ]
};

type AudienceType = "customer" | "branch" | "user" | "global";

type AudienceEntity = {
  entityId?: number;
  type: AudienceType;
};

interface NotificationsTabProps {
  terminology: ITerminology;
  notificationType: "manual" | "system";
}

/**
 * Main "Notifications" View
 *
 * @returns
 */
const NotificationsTab: React.ReactFCWithChildren<NotificationsTabProps> = props => {
  const { terminology } = props;
  const [ listNotificationsResponse, listNotifications ] = useNotificationsApi("LIST_NOTIFICATIONS");
  const history = useHistory();
  const [ selectedUsers, setSelectedUsers ] = React.useState<number[]>([]);
  const [ audienceType, setAudienceType ] = React.useState<AudienceType | undefined>();
  const [ audienceEntities, setAudienceEntities ] = React.useState<AudienceEntity[]>([]);
  const [ notifiedUsersIds, setNotifiedUsersIds ] = React.useState<number[]>([]);

  //in onFetch, the filters are added in depending if they exist in the reactState.
  const onFetchData = React.useCallback((params: {filters: FilterParams<INotification>, pagination: PaginatedParams<INotification>}) => {
    if (audienceEntities.length) {
      params.filters = {
        ...params.filters,
        notificationRecipients: audienceEntities
      };
    }

    if (props.notificationType === "manual") {
      selectedUsers.length ? params.filters.sentByUserId = { $in: selectedUsers } : delete params.filters.sentByUserId;
    }

    if (props.notificationType === "system") {
      params.filters.sentByUserId = null;
    }

    notifiedUsersIds?.length ? params.filters.notificationUserIds = notifiedUsersIds : null;

    listNotifications({
      pagination: params.pagination,
      filters: params.filters
    });
  }, [
    listNotifications,
    audienceEntities,
    selectedUsers,
    notifiedUsersIds,
    props.notificationType
  ]);

  /**
   * Table actions
   */
  const tableActions = React.useCallback((item: CellContext<INotification, unknown>) => {
    const actions = [
      {
        title: "View",
        action: () => history.push(`/platform/notifications/${item.row.original.id}/details`),
        newTabPath: `/platform/notifications/${item.row.original.id}/details`

      }
    ];

    return actions;
  }, [ history ]);

  const columns = React.useMemo(() => {
    return getNotificationTableColumns({
      terminology,
      action: info => {
        return (
          <ActionButton
            title="actions"
            items={tableActions(info)}
          />
        );
      },
      excludedColumns: props.notificationType === "system" ? [ "sentBy" ] : undefined
    });
  }, [
    tableActions,
    terminology,
    props.notificationType
  ]);

  //this determines which drop down to display depending on the audience type selected.
  const AudienceSelector = React.useMemo(() => {
    switch (audienceType) {
      case "branch":
        return (
          <DropMultiSelect
            onDropsChange={change => {
              setAudienceEntities(change.dropsSelected.map(id => ({
                entityId: parseInt(id, 10),
                type: audienceType
              })));
            }}
          />
        );
      case "user":
        return (
          <UserMultiSelect
            labelOverride="Sent to User"
            key="search"
            onUsersChange={change => {
              setNotifiedUsersIds(change.allUsersSelected.map(user => user.id));
            }}
            userFilters={{ customerId: { $not: null } }}
          />

        );
      case "customer":
        return (
          <CustomerMultiSelect
            onCustomersChange= {change => {
              setAudienceEntities(change.customersSelected.map(id => ({
                entityId: parseInt(id, 10),
                type: audienceType
              })));
            }}
          />
        );
      case "global":
        setAudienceEntities([
          {
            entityId: undefined,
            type: "global"
          }
        ]);

        return <></>;
      default: return <></>;
    }
  }, [ audienceType ]);

  return (
    <TableWrapper>
      <Text as="h4">
        Quick Filters
      </Text>

      <FiltersWrapper>

        {props.notificationType === "manual" && (
          <UserMultiSelect
            labelOverride="Sent by Admin"
            key="search"
            onUsersChange={change => {
              setSelectedUsers(change.usersSelected.map(id => parseInt(id, 10)));
            }}
            userFilters={{ customerId: null }}
          />
        )}

        <AudienceTypeWrapper>
          <div className="audience-select">
            <Select
              as="select"
              name="audienceType"
              label="Audience"
              handleChange={e => {
                setAudienceType(e.target.value);

                if (audienceEntities.length || notifiedUsersIds.length) {
                  setAudienceEntities([]);
                  setNotifiedUsersIds([]);
                }
              }}

              options={[
                {
                  value: "customer",
                  text: _.capitalize(terminology.customer.plural.text)
                },
                {
                  value: "branch",
                  text: _.capitalize(terminology.drop.plural.text)
                },
                {
                  value: "user",
                  text: _.capitalize(terminology.user.plural.text)
                },
                {
                  value: "global",
                  text: "Global"
                }
              ]}

              unselectedOption= {{
                value: "",
                text: "None"
              }}

              value = {audienceType || ""}
            />

          </div>
        </AudienceTypeWrapper>

        {AudienceSelector}

      </FiltersWrapper>

      <Table
        tableOf={terminology.notification.plural.text}
        onFetchData={onFetchData}
        columns={columns}
        loading={listNotificationsResponse.pending}
        error={listNotificationsResponse.error}
        defaultPagination={defaultPagination}
        data={listNotificationsResponse.data?.result ? listNotificationsResponse.data?.result : []}
        pageCount={listNotificationsResponse.data?.pages}
        rowCount={listNotificationsResponse.data?.count}
      />

    </TableWrapper>

  );
};

const TableWrapper = styled.div`
${({ theme }) => css`
    margin-bottom: ${theme.spacing.margins.md};
    padding: ${theme.spacing.padding.md};
    background: ${theme.colors.white};
    border-radius: 4px;
  `};
`;

const FiltersWrapper = styled.div`
${() => css`
    display: flex;
    flex-direction: row;
    grid-column-gap: 20px;
`};
`;

const AudienceTypeWrapper = styled.div`
${({ theme }) => css`
  display:flex;
  flex-direction: row;
  padding-bottom: ${theme.spacing.padding.sm};
  margin-right: 0px;

  .select {
    height: 40px
  }

  .clear-audience {
    display: flex;
    flex-direction: column;
    margin-left: ${theme.spacing.padding.xs};
    margin-right: ${theme.spacing.padding.xs};
  }
  
  .clear-audience svg {
    margin-top: auto;
    margin-bottom: ${theme.spacing.padding.xs};
  }

`};
`;

export default NotificationsTab;